import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { HelpTip } from '@/components/ui/help-tip';
import { cn } from '@/lib/utils';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { t } from '@/i18n';
import { getCarrier } from '@/data/arrivalsData';
import { siteNow, SITE_LIMIT_MIN } from '@/lib/siteNow';

const minLabel = (m) => (m == null ? '–' : `${m} min`);

// "Areál teď" (#115): trucks on site by place, longest first; over the limit in red.
export const SiteNow = ({ loadings, clock, onSelect }) => {
  const { zones, total, late } = useMemo(() => siteNow(loadings, clock), [loadings, clock]);

  return (
    <div className="space-y-4">
      <Card className={cn('p-4 flex items-center gap-3', late ? 'border-red-300 bg-red-50' : '')}>
        {late > 0 && <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" aria-hidden="true" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-1">
            {t('site.title')} · {clock} <HelpTip id="siteNow" />
          </div>
          <div className={cn('text-sm', late ? 'text-red-800' : 'text-muted-foreground')}>
            {t('site.summary').replace('{total}', total)}
            {late > 0 && ` ${t('site.lateSummary').replace('{late}', late).replace('{limit}', SITE_LIMIT_MIN)}`}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {zones.map(zone => (
          <Card key={zone.key} className="p-4 space-y-2" data-testid={`site-${zone.key}`}>
            <div className="flex items-baseline justify-between">
              <div className="text-sm font-semibold">{t(`site.zone.${zone.key}`)}</div>
              <div className="text-xs text-muted-foreground tabular-nums">{zone.trucks.length}</div>
            </div>
            {zone.trucks.length === 0 && <div className="text-sm text-muted-foreground">{t('site.empty')}</div>}
            {zone.trucks.map(({ loading, here, onSite, late: isLate }) => (
              <button
                key={loading.id}
                type="button"
                onClick={() => onSelect(loading)}
                className={cn('w-full text-left rounded-md border p-2 flex items-center gap-2 hover:bg-muted/50',
                  isLate ? 'border-red-300 bg-red-50' : 'border-border')}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{loading.plate}</div>
                  <div className="text-xs text-muted-foreground truncate">{getCarrier(loading.carrierId).name}</div>
                  {isLate && <div className="text-xs text-red-800 mt-0.5">{t('site.lateHint')}</div>}
                </div>
                <div className="text-right shrink-0 tabular-nums">
                  <div className="text-sm font-semibold">{minLabel(here)}</div>
                  <div className={cn('text-[11px]', isLate ? 'text-red-800 font-semibold' : 'text-muted-foreground')}>
                    {t('site.onSite')} {minLabel(onSite)}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              </button>
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
};
